import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    CircularProgress,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Select,
    FormControl,
    Modal,
    Tabs,
    Tab,
    Radio,
    RadioGroup,
    FormControlLabel,
    Divider,
    Tooltip,
    Snackbar,
    Alert,
    InputAdornment,
    Skeleton
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
    searchCandidates,
    getCandidateResume,
    downloadCandidateResume,
    createInterview,
    getCandidateIntroVideo,
    updateCandidateStatus
} from "../../Api/CandidateApi";

const BRAND = '#6B31C4';
const BRAND_DARK = '#5a29a5';
const STATUS_OPTIONS = ['applied', 'reviewed', 'shortlisted', 'rejected', 'hired'];

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0].substring(0, 2).toUpperCase();
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Create Interview Modal ──────────────────────────────────────────────────
const TIMEZONES = [
    'Asia/Kolkata', 'UTC', 'America/New_York', 'America/Los_Angeles',
    'Europe/London', 'Asia/Singapore', 'Asia/Tokyo',
];
const DURATION_OPTIONS = [
    { label: '15 minutes', value: 15 },
    { label: '30 minutes', value: 30 },
    { label: '45 minutes', value: 45 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 },
];
const HOURS_12 = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MINUTES = ['00', '15', '30', '45'];

const getInitialDateTime = () => {
    const now = new Date();
    const defaultTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour in future
    const yyyy = defaultTime.getFullYear();
    const mm = String(defaultTime.getMonth() + 1).padStart(2, '0');
    const dd = String(defaultTime.getDate()).padStart(2, '0');
    
    let rawHour = defaultTime.getHours();
    const period = rawHour >= 12 ? 'PM' : 'AM';
    let hour12 = rawHour % 12;
    if (hour12 === 0) hour12 = 12;
    
    return {
        date: `${yyyy}-${mm}-${dd}`,
        hour: String(hour12).padStart(2, '0'),
        minute: '00',
        period: period
    };
};

function ScheduleInterviewPanel({ candidate, onClose, onSuccess }) {
    const initialDt = getInitialDateTime();
    const [form, setForm] = useState({
        durationMinutes: 30, mode: 'online', message: '',
        meetingLink: '', locationText: '', date: initialDt.date, hour: initialDt.hour, minute: initialDt.minute,
        period: initialDt.period, timeZone: 'Asia/Kolkata',
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async () => {
        if (!form.date || !form.message) { setError('Date and message are required.'); return; }
        
        if (form.mode === 'online') {
            if (!form.meetingLink || !form.meetingLink.trim()) {
                setError('Meeting link is required for online interviews.');
                return;
            }
            try {
                const trimmedLink = form.meetingLink.trim();
                const urlToTest = trimmedLink.match(/^https?:\/\//i) ? trimmedLink : `https://${trimmedLink}`;
                const parsedUrl = new URL(urlToTest);
                if (!parsedUrl.hostname || !parsedUrl.hostname.includes('.')) {
                    throw new Error();
                }
            } catch (e) {
                setError('Please enter a valid meeting link URL (e.g., https://meet.google.com/abc-defg-hij).');
                return;
            }
        }

        if (form.mode === 'in-person') {
            if (!form.locationText || !form.locationText.trim()) {
                setError('Address / Location is required for in-person interviews.');
                return;
            }
            if (!/[a-zA-Z0-9]/.test(form.locationText)) {
                setError('Please enter a valid address/location. It cannot contain only special characters.');
                return;
            }
        }

        const hourVal = parseInt(form.hour, 10);
        let hour24 = hourVal;
        if (form.period === 'PM' && hourVal !== 12) {
            hour24 += 12;
        } else if (form.period === 'AM' && hourVal === 12) {
            hour24 = 0;
        }
        const hourStr = String(hour24).padStart(2, '0');

        const selectedStart = new Date(`${form.date}T${hourStr}:${form.minute}:00`);
        const now = new Date();
        selectedStart.setSeconds(0, 0);
        now.setSeconds(0, 0);
        if (selectedStart < now) {
            setError('Cannot schedule interviews in the past.');
            return;
        }
        setSubmitting(true); setError('');
        try {
            const interviewStartAt = selectedStart.toISOString();

            // Decodes the JWT token to fetch employer ID dynamically
            let employerId = localStorage.getItem('employerId');
            if (!employerId) {
                const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                if (token) {
                    try {
                        const base64Url = token.split('.')[1];
                        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                        const jsonPayload = decodeURIComponent(
                            window.atob(base64)
                                .split('')
                                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                                .join('')
                        );
                        employerId = JSON.parse(jsonPayload).id;
                    } catch (e) {
                        console.error("Token decode error:", e);
                    }
                }
            }

            await createInterview({
                jobId: candidate?._raw?.jobId || candidate?._raw?.job?.id,
                applicationId: candidate?._raw?.id,
                candidateId: candidate?.userId || candidate?._raw?.userId,
                createdByEmployerId: employerId ? Number(employerId) : null,
                interviewStartAt,
                status: 'scheduled',
                mode: form.mode,
                timeZone: form.timeZone,
                durationMinutes: Number(form.durationMinutes),
                meetingLink: form.meetingLink || null,
                locationText: form.locationText || null,
                message: form.message,
            });
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send interview request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const labelSx = { fontSize: '0.8rem', fontWeight: 600, color: '#555', mb: 0.5, display: 'block' };

    return (
        <Box sx={{ p: 4, overflowY: 'auto', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Schedule with {candidate?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                Coordinate an interview slot and send a personalized note.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Duration */}
            <Typography sx={labelSx}>Duration *</Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <Select size="small" value={form.durationMinutes} onChange={handleChange('durationMinutes')}>
                    {DURATION_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </Select>
            </FormControl>

            {/* Mode */}
            <Typography sx={labelSx}>Mode *</Typography>
            <RadioGroup row value={form.mode} onChange={handleChange('mode')} sx={{ mb: 2 }}>
                {[{ label: 'Online', value: 'online' }, { label: 'In-person', value: 'in-person' }, { label: 'Phone', value: 'phone' }].map(m => (
                    <FormControlLabel key={m.value} value={m.value} control={<Radio size="small" sx={{ color: BRAND, '&.Mui-checked': { color: BRAND } }} />} label={m.label} />
                ))}
            </RadioGroup>

            {/* Message */}
            <Typography sx={labelSx}>Write a message *</Typography>
            <TextField fullWidth multiline rows={3} size="small" placeholder="Write a message *"
                value={form.message} onChange={handleChange('message')} sx={{ mb: 2 }} />

            {/* Meeting Link (for Online mode) */}
            {form.mode === 'online' && (
                <>
                    <Typography sx={labelSx}>Meeting link *</Typography>
                    <TextField fullWidth size="small" placeholder="Meeting link *"
                        value={form.meetingLink} onChange={handleChange('meetingLink')} sx={{ mb: 2 }} />
                </>
            )}

            {/* Address / Location (for In-person mode) */}
            {form.mode === 'in-person' && (
                <>
                    <Typography sx={labelSx}>Address / Location *</Typography>
                    <TextField fullWidth size="small" placeholder="Enter physical address/location *"
                        value={form.locationText} onChange={handleChange('locationText')} sx={{ mb: 2 }} />
                </>
            )}

            {/* Date / Hour / Minute / AM-PM */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <Box sx={{ flex: 2 }}>
                    <Typography sx={labelSx}>Date *</Typography>
                    <TextField fullWidth size="small" type="date" value={form.date}
                        onChange={handleChange('date')}
                        inputProps={{ 
                            min: (() => {
                                const today = new Date();
                                const yyyy = today.getFullYear();
                                const mm = String(today.getMonth() + 1).padStart(2, '0');
                                const dd = String(today.getDate()).padStart(2, '0');
                                return `${yyyy}-${mm}-${dd}`;
                            })()
                        }}
                        InputLabelProps={{ shrink: true }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Hour *</Typography>
                    <FormControl fullWidth>
                        <Select size="small" value={form.hour} onChange={handleChange('hour')}>
                            {HOURS_12.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Minute *</Typography>
                    <FormControl fullWidth>
                        <Select size="small" value={form.minute} onChange={handleChange('minute')}>
                            {MINUTES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>AM/PM *</Typography>
                    <FormControl fullWidth>
                        <Select size="small" value={form.period} onChange={handleChange('period')}>
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Timezone */}
            <Typography sx={labelSx}>Time Zone *</Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
                <Select size="small" value={form.timeZone} onChange={handleChange('timeZone')}>
                    {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
                </Select>
            </FormControl>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 8, textTransform: 'none', borderColor: '#ccc', color: '#555' }}>
                    Cancel
                </Button>
                <Button variant="contained" disableElevation onClick={handleSubmit} disabled={submitting}
                    sx={{ borderRadius: 8, textTransform: 'none', bgcolor: BRAND, '&:hover': { bgcolor: BRAND_DARK } }}>
                    {submitting ? 'Sending...' : 'Send Request'}
                </Button>
            </Box>
        </Box>
    );
}

// ─── Candidate Detail Modal ──────────────────────────────────────────────────
function CandidateDetailModal({ open, onClose, candidate, onInterviewCreated }) {
    const [tab, setTab] = useState(0);
    const [resumeUrl, setResumeUrl] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [loadingResume, setLoadingResume] = useState(false);
    const [showInterview, setShowInterview] = useState(false);
    const [copied, setCopied] = useState('');
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => {
        if (!open) {
            setShowInterview(false);
            return;
        }
        if (!candidate) return;
        setTab(0); setResumeUrl(null); setVideoUrl(null); setShowInterview(false);
        fetchResume();
    }, [candidate, open]);

    const fetchResume = async () => {
        if (!candidate) return;
        setLoadingResume(true);
        try {
            const userId = candidate.userId || (candidate.candidate && candidate.candidate.id) || candidate._id || candidate.id;
            const jobId = candidate.jobId || (candidate.job && candidate.job.id) || candidate._raw?.jobId || candidate._raw?.job?.id;
            const data = await getCandidateResume(userId, jobId);
            if (data?.success && data?.data?.resumeUrl) {
                setResumeUrl(data.data.resumeUrl);
            }
        } catch (e) { /* no resume */ }
        finally { setLoadingResume(false); }
    };

    const handleVideoTab = async () => {
        if (videoUrl) return;
        try {
            const userId = candidate.userId || (candidate.candidate && candidate.candidate.id) || candidate._id || candidate.id;
            const data = await getCandidateIntroVideo(userId);
            if (data?.success && data?.data?.videoUrl) setVideoUrl(data.data.videoUrl);
        } catch (e) { /* no video */ }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopied(field);
        setSnack({
            open: true,
            msg: `${field === 'email' ? 'Email' : 'Phone number'} copied successfully!`,
            severity: 'success'
        });
        setTimeout(() => setCopied(''), 2000);
    };

    const handleDownload = () => {
        const userId = candidate?.userId || (candidate?.candidate && candidate.candidate.id) || candidate?._id || candidate?.id;
        if (userId) downloadCandidateResume(userId);
    };

    if (!candidate) return null;
    const name = candidate.name || 'Candidate';
    const jobTitle = candidate.jobTitle || '';
    const location = candidate.location || '';
    const email = candidate.email || '';
    const phone = candidate.phone || candidate.phoneNumber || candidate.mobile || '';
    const status = candidate.status || 'applied';
    const skills = candidate.skills || [];

    return (
        <>
            <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
                <Box sx={{
                    width: { xs: '98vw', md: '90vw' }, maxWidth: 1000,
                    height: { xs: '95vh', md: '85vh' },
                    bgcolor: '#fff', borderRadius: 2, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column', boxShadow: 24,
                }}>
                    {/* Header */}
                    <Box sx={{ background: BRAND, color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#fff', color: BRAND, fontWeight: 700, width: 44, height: 44 }}>
                            {getInitials(name)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#ffffff' }}>{name}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                {jobTitle}{jobTitle && location ? ' at ' : ''}{location}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button variant="contained" size="small" startIcon={<EventIcon />}
                                onClick={() => setShowInterview(true)}
                                sx={{ bgcolor: '#fff', color: BRAND, '&:hover': { bgcolor: '#f0e6ff' }, textTransform: 'none', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600 }}>
                                Create Interview
                            </Button>
                            <Button variant="contained" size="small" startIcon={<CloseIcon />}
                                onClick={onClose}
                                sx={{ bgcolor: '#e53935', '&:hover': { bgcolor: '#c62828' }, textTransform: 'none', borderRadius: 8, fontSize: '0.8rem' }}>
                                Close
                            </Button>
                        </Box>
                    </Box>

                    {/* Body */}
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Left Panel */}
                        <Box sx={{ width: 240, borderRight: '1px solid #eee', p: 2.5, overflowY: 'auto', flexShrink: 0 }}>
                            <Typography variant="overline" sx={{ fontWeight: 700, color: '#333', letterSpacing: 1.5, display: 'block', mb: 1.5 }}>
                                CANDIDATE DETAILS
                            </Typography>

                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <WorkIcon sx={{ fontSize: 16, color: BRAND }} />
                                    <Typography variant="caption" color="text.secondary">Job:</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, pl: 3 }}>{jobTitle || '—'}</Typography>
                            </Box>

                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <LocationOnIcon sx={{ fontSize: 16, color: BRAND }} />
                                    <Typography variant="caption" color="text.secondary">Location:</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, pl: 3 }}>{location || '—'}</Typography>
                            </Box>

                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <EmailIcon sx={{ fontSize: 16, color: BRAND }} />
                                    <Typography variant="caption" color="text.secondary">Email:</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 3 }}>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>{email || '—'}</Typography>
                                    {email && (
                                        <Tooltip title={copied === 'email' ? 'Copied!' : 'Copy'}>
                                            <IconButton size="small" onClick={() => copyToClipboard(email, 'email')}>
                                                <ContentCopyIcon sx={{ fontSize: 14, color: copied === 'email' ? 'green' : '#999' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <PhoneIcon sx={{ fontSize: 16, color: BRAND }} />
                                    <Typography variant="caption" color="text.secondary">Phone:</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 3 }}>
                                    <Typography variant="body2" sx={{ flex: 1 }}>{phone || '—'}</Typography>
                                    {phone && (
                                        <Tooltip title={copied === 'phone' ? 'Copied!' : 'Copy'}>
                                            <IconButton size="small" onClick={() => copyToClipboard(phone, 'phone')}>
                                                <ContentCopyIcon sx={{ fontSize: 14, color: copied === 'phone' ? 'green' : '#999' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Status:</Typography>
                                <Typography variant="body2" sx={{
                                    display: 'inline-block', px: 1.5, py: 0.5, borderRadius: 1, textTransform: 'capitalize',
                                    bgcolor: status === 'hired' ? '#e8f5e9' : status === 'rejected' ? '#ffebee' : status === 'shortlisted' ? '#fff3e0' : status === 'reviewed' ? '#f3e5f5' : '#e3f2fd',
                                    color: status === 'hired' ? '#2e7d32' : status === 'rejected' ? '#c62828' : status === 'shortlisted' ? '#ef6c00' : status === 'reviewed' ? '#6a1b9a' : '#1565c0',
                                    fontWeight: 600
                                }}>
                                    {status}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Skills:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {skills.length > 0 ? skills.map((s, i) => (
                                        <Box key={i} sx={{ px: 1, py: 0.2, bgcolor: '#f0f0f0', borderRadius: 1, fontSize: '0.75rem', color: '#555' }}>
                                            {s.name || s}
                                        </Box>
                                    )) : (
                                        <Typography variant="body2" color="text.secondary">No skills listed</Typography>
                                    )}
                                </Box>
                            </Box>

                        </Box>

                        {/* Right Panel */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {showInterview ? (
                                /* ── Inline Schedule Interview Form ── */
                                <ScheduleInterviewPanel
                                    key={candidate?.id || candidate?._id}
                                    candidate={candidate}
                                    onClose={() => setShowInterview(false)}
                                    onSuccess={() => {
                                        setShowInterview(false);
                                        setSnack({ open: true, msg: 'Interview request sent successfully!', severity: 'success' });
                                        onInterviewCreated?.();
                                    }}
                                />
                            ) : (
                                <>
                                    {/* Tabs */}
                                    <Box sx={{ borderBottom: '1px solid #eee', px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 1) handleVideoTab(); }}
                                            TabIndicatorProps={{ style: { backgroundColor: BRAND } }}>
                                            <Tab label="RESUME" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', color: tab === 0 ? BRAND : '#555', '&.Mui-selected': { color: BRAND } }} />
                                            <Tab icon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="VIDEO INTRO"
                                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', color: 'error.main', '&.Mui-selected': { color: 'error.main' } }} />
                                        </Tabs>
                                        <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
                                            sx={{ textTransform: 'none', color: BRAND, fontWeight: 600 }}>
                                            Download
                                        </Button>
                                    </Box>

                                    {/* Content */}
                                    <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f8f8f8', p: 2 }}>
                                        {tab === 0 && (
                                            loadingResume ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                                                    <CircularProgress sx={{ color: BRAND }} />
                                                </Box>
                                            ) : resumeUrl ? (
                                                <Box sx={{ width: '100%', height: 'calc(100vh - 220px)', minHeight: '550px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
                                                        <Button 
                                                            size="small" 
                                                            variant="text" 
                                                            onClick={() => window.open(resumeUrl, '_blank')}
                                                            sx={{ textTransform: 'none', color: BRAND, fontWeight: 600 }}
                                                        >
                                                            Open in New Tab ↗
                                                        </Button>
                                                    </Box>
                                                    <iframe
                                                        src={resumeUrl}
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: '1px solid #ddd', borderRadius: 8, flex: 1 }}
                                                        title="Resume PDF"
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ textAlign: 'center', pt: 8 }}>
                                                    <Typography color="text.secondary">No resume available for this candidate.</Typography>
                                                </Box>
                                            )
                                        )}
                                        {tab === 1 && (
                                            videoUrl ? (
                                                <video controls width="100%" style={{ borderRadius: 8 }} src={videoUrl}>
                                                    Your browser does not support the video tag.
                                                </video>
                                            ) : (
                                                <Box sx={{ textAlign: 'center', pt: 8 }}>
                                                    <Typography color="text.secondary">No video intro available for this candidate.</Typography>
                                                </Box>
                                            )
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity}>{snack.msg}</Alert>
            </Snackbar>
        </>
    );
}

// ─── Main Candidates Component ───────────────────────────────────────────────
const Candidates = () => {
    // Raw job application rows from API
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter inputs
    const [jobTitleFilter, setJobTitleFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchName, setSearchName] = useState('');

    // Server-side pagination metadata
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Selection
    const [selected, setSelected] = useState([]);

    // Pagination (1-based to match API)
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(24);

    // Active API filters (applied only when Search is clicked)
    const [apiFilters, setApiFilters] = useState({});

    useEffect(() => {
        if (searchName === '' && apiFilters.search) {
            setApiFilters(prev => {
                const next = { ...prev };
                delete next.search;
                return next;
            });
            setPage(1);
        }
    }, [searchName, apiFilters.search]);

    useEffect(() => {
        if (jobTitleFilter === '' && apiFilters.title) {
            setApiFilters(prev => {
                const next = { ...prev };
                delete next.title;
                return next;
            });
            setPage(1);
        }
    }, [jobTitleFilter, apiFilters.title]);

    useEffect(() => {
        if (locationFilter === '' && apiFilters.location) {
            setApiFilters(prev => {
                const next = { ...prev };
                delete next.location;
                return next;
            });
            setPage(1);
        }
    }, [locationFilter, apiFilters.location]);

    useEffect(() => {
        if (statusFilter === '' && apiFilters.status) {
            setApiFilters(prev => {
                const next = { ...prev };
                delete next.status;
                return next;
            });
            setPage(1);
        }
    }, [statusFilter, apiFilters.status]);

    // Row menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuCandidate, setMenuCandidate] = useState(null);

    // Detail modal
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailCandidate, setDetailCandidate] = useState(null);

    // Snack
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => { fetchCandidates(apiFilters, page, rowsPerPage); }, [apiFilters, page, rowsPerPage]);

    const fetchCandidates = async (filters = {}, pg = 1, limit = 24) => {
        setLoading(true); setError(null);
        try {
            const data = await searchCandidates({ ...filters, page: pg, limit });
            if (data.success) {
                const apps = data.jobApplications || [];
                setApplications(apps);
                
                let correctedTotal = data.total || 0;
                if (pg === 1 && apps.length < limit) {
                    correctedTotal = apps.length;
                } else if (apps.length > 0 && correctedTotal < (pg - 1) * limit + apps.length) {
                    correctedTotal = (pg - 1) * limit + apps.length;
                }
                
                setTotal(correctedTotal);
                setTotalPages(Math.ceil(correctedTotal / limit));
            } else {
                setError('Failed to fetch candidates');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch candidates');
        } finally { setLoading(false); }
    };

    const handleSearch = () => {
        const filters = {};
        if (jobTitleFilter) filters.title = jobTitleFilter;
        if (locationFilter) filters.location = locationFilter;
        if (statusFilter) filters.status = statusFilter;
        if (searchName) filters.search = searchName;
        setApiFilters(filters);
        setPage(1); // reset to first page on new search
    };

    const handleClear = () => {
        setJobTitleFilter('');
        setLocationFilter('');
        setStatusFilter('');
        setSearchName('');
        setApiFilters({});
        setSelected([]);
        setPage(1);
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

    // Flatten application rows: expose candidate + job fields at top level for easy rendering
    const rows = applications.map(app => ({
        id: app.id,
        userId: app.userId,
        resumeId: app.resumeId,   // ← actual resume ID, NOT the application id
        // candidate info
        name: app.candidate?.name || '',
        email: app.candidate?.email || '',
        avatarUrl: app.candidate?.personalDetails?.profile || app.candidate?.avatarUrl || '',
        location: app.candidate?.preferredLocations?.[0]?.location?.name || '',
        skills: app.candidate?.userSkills?.map(us => us.skill) || app.candidate?.skills || [],
        experience: app.candidate?.personalDetails?.experience || app.candidate?.experience || null,
        phone: app.candidate?.phone || app.candidate?.phoneNumber || app.candidate?.mobile || '',
        // job info
        jobTitle: app.job?.title || '',
        // application info
        status: app.currentStatus || 'applied',
        appliedAt: app.appliedAt || app.createdAt,
        // keep original for modals
        _raw: app,
    }));

    const handleSelectAll = (e) => {
        setSelected(e.target.checked ? rows.map(r => r.id) : []);
    };

    const handleSelectRow = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleMenuOpen = (e, candidate) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setMenuCandidate(candidate);
    };

    const handleMenuClose = () => { setAnchorEl(null); setMenuCandidate(null); };

    const handleView = () => {
        setDetailCandidate(menuCandidate);
        setDetailOpen(true);
        handleMenuClose();
    };

    const handleDownload = () => {
        if (menuCandidate) {
            const userId = menuCandidate.userId || (menuCandidate.candidate && menuCandidate.candidate.id) || menuCandidate._id || menuCandidate.id;
            downloadCandidateResume(userId);
        }
        handleMenuClose();
    };

    const handleStatusChange = async (applicationId, newStatus) => {
        try {
            const res = await updateCandidateStatus(applicationId, newStatus);
            if (res.success) {
                setSnack({ open: true, msg: 'Status updated successfully', severity: 'success' });
                setApplications(prev => prev.map(app => 
                    app.id === applicationId ? { ...app, currentStatus: newStatus } : app
                ));
            } else {
                setSnack({ open: true, msg: res.message || 'Failed to update status', severity: 'error' });
            }
        } catch (error) {
            setSnack({ open: true, msg: error.response?.data?.message || 'Error updating status', severity: 'error' });
        }
    };

    // Pagination display helpers (API is 1-based)
    const startCount = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
    const endCount = Math.min(page * rowsPerPage, total);

    return (
        <Box sx={{ backgroundColor: '#F8F9FA', minHeight: '100vh', pb: 8 }}>
            {/* Header */}
            <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 }, pb: 2, maxWidth: '1400px', mx: 'auto' }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>Candidates</Typography>
            </Box>

            <Container sx={{ mt: 0, mb: 4, px: { xs: 1, md: 2 }, maxWidth: '1400px !important', mx: 'auto' }}>
                {/* Filter Bar */}
                <Paper elevation={0} sx={{ mb: 0, p: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', borderRadius: '12px 12px 0 0', border: '1px solid #E5E7EB', borderBottom: 'none' }}>
                    <TextField size="small" placeholder="Job Title" value={jobTitleFilter}
                        onChange={e => setJobTitleFilter(e.target.value)} onKeyDown={handleKeyDown}
                        sx={{ flex: 1, minWidth: 120 }} />
                    <TextField size="small" placeholder="Location" value={locationFilter}
                        onChange={e => setLocationFilter(e.target.value)} onKeyDown={handleKeyDown}
                        sx={{ flex: 1, minWidth: 120 }} />
                    <TextField size="small" placeholder="Status" value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)} onKeyDown={handleKeyDown}
                        sx={{ flex: 1, minWidth: 120 }} />
                    <TextField size="small" placeholder="Search candidate Name" value={searchName}
                        onChange={e => setSearchName(e.target.value)} onKeyDown={handleKeyDown}
                        InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ color: '#aaa' }} /></InputAdornment> }}
                        sx={{ flex: 2, minWidth: 180 }} />
                    <Button variant="contained" disableElevation onClick={handleSearch}>
                        Search
                    </Button>
                    <Button variant="outlined" disableElevation onClick={handleClear}>
                        Clear
                    </Button>
                </Paper>

                {/* Table */}
                <Paper elevation={0} sx={{ borderRadius: '0 0 12px 12px', overflow: 'hidden', border: '1px solid #E5E7EB', borderTop: 'none' }}>
                    {error ? (
                        <Box sx={{ p: 6, textAlign: 'center' }}><Typography color="error">{error}</Typography></Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table sx={{ minWidth: 750 }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                                            <TableCell padding="checkbox" sx={{ width: 48 }}>
                                                <Checkbox
                                                    indeterminate={selected.length > 0 && selected.length < rows.length}
                                                    checked={rows.length > 0 && selected.length === rows.length}
                                                    onChange={handleSelectAll}
                                                    disabled={loading}
                                                    sx={{ color: '#D1D5DB', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#6B31C4' } }}
                                                />
                                            </TableCell>
                                            {['Job Applied', 'Candidate', 'Experience', 'Status', 'More'].map((h, i) => (
                                                <TableCell key={h} align={i === 4 ? 'center' : 'left'}
                                                    sx={{ color: '#6B7280', fontWeight: 600, py: 1.5, whiteSpace: 'nowrap', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {h}
                                                        {(h === 'Candidate' || h === 'Experience') && <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.7 }} />}
                                                    </Box>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            Array.from(new Array(5)).map((_, index) => (
                                                <TableRow key={index}>
                                                    <TableCell padding="checkbox">
                                                        <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5 }} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Skeleton variant="text" width="80%" height={18} />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Skeleton variant="circular" width={38} height={38} />
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Skeleton variant="text" width="60%" height={18} />
                                                                <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.5 }} />
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Skeleton variant="text" width="50%" height={18} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1.5 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Skeleton variant="circular" width={28} height={28} sx={{ mx: 'auto' }} />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                                    <Typography color="text.secondary">No candidates found.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : rows.map(row => {
                                            const isSelected = selected.includes(row.id);
                                            return (
                                                <TableRow hover key={row.id} selected={isSelected}
                                                    sx={{ '&:hover': { bgcolor: '#fafafa' }, cursor: 'default' }}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox checked={isSelected} onChange={() => handleSelectRow(row.id)}
                                                            sx={{ color: '#ccc', '&.Mui-checked': { color: BRAND } }} />
                                                    </TableCell>
                                                    {/* Job Applied cell */}
                                                    <TableCell sx={{ minWidth: 160 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <WorkIcon sx={{ fontSize: 13, color: '#888' }} />
                                                            <Typography variant="body2" sx={{ color: '#3a7bd5', fontWeight: 500 }}>
                                                                {row.jobTitle || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    {/* Candidate cell */}
                                                    <TableCell sx={{ py: 1.5, minWidth: 200 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                            <Avatar src={row.avatarUrl || undefined} sx={{ bgcolor: BRAND, width: 38, height: 38, fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                                                                {!row.avatarUrl && getInitials(row.name)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#3a7bd5', mb: 0.2 }}>
                                                                    {row.name || '—'}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2 }}>
                                                                    <LocationOnIcon sx={{ fontSize: 13, color: '#888' }} />
                                                                    <Typography variant="caption" color="text.secondary">{row.location || '—'}</Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                                    <CalendarMonthIcon sx={{ fontSize: 13, color: '#888' }} />
                                                                    <Typography variant="caption" color="text.secondary">{formatDate(row.appliedAt) || '—'}</Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    {/* Experience cell */}
                                                    <TableCell sx={{ minWidth: 120 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {row.experience ? `${row.experience} yrs` : 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    {/* Status */}
                                                    <TableCell sx={{ minWidth: 140 }}>
                                                        <FormControl size="small">
                                                            <Select value={row.status || 'applied'}
                                                                onChange={(e) => handleStatusChange(row._raw.id, e.target.value)}
                                                                sx={{
                                                                    fontSize: '0.8rem', borderRadius: 8, textTransform: 'capitalize',
                                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bbb' }
                                                                }}>
                                                                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>{s.replace('_', ' ')}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                    {/* More */}
                                                    <TableCell align="center">
                                                        <IconButton size="small" onClick={e => handleMenuOpen(e, row)}>
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination row */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                bgcolor: '#eef0f8', px: 2, py: 1, borderTop: '1px solid #ddd',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        &lt; {startCount} - {endCount} of {total} &gt;
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
                                    <FormControl size="small">
                                        <Select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                                            sx={{ fontSize: '0.75rem', '.MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' } }}>
                                            {[10, 24, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                        <Typography sx={{ fontSize: '1rem', color: page <= 1 ? '#ccc' : BRAND }}>‹</Typography>
                                    </IconButton>
                                    <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                                        <Typography sx={{ fontSize: '1rem', color: page >= totalPages ? '#ccc' : BRAND }}>›</Typography>
                                    </IconButton>
                                </Box>
                            </Box>
                        </>
                    )}
                </Paper>
            </Container>

            {/* 3-dot Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
                PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 140 } }}>
                <MenuItem onClick={handleView} sx={{ gap: 1.5, fontSize: '0.9rem' }}>
                    <VisibilityIcon sx={{ fontSize: 18, color: BRAND }} /> View
                </MenuItem>
                <MenuItem onClick={handleDownload} sx={{ gap: 1.5, fontSize: '0.9rem' }}>
                    <DownloadIcon sx={{ fontSize: 18, color: BRAND }} /> Download
                </MenuItem>
            </Menu>

            {/* Candidate Detail Modal */}
            <CandidateDetailModal
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                candidate={detailCandidate}
                onInterviewCreated={() => fetchCandidates(apiFilters, page, rowsPerPage)}
            />

            <Snackbar open={snack.open} autoHideDuration={3000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Candidates;
